import React from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export default function SubscriptionBanner() {
  const { t } = useLanguage();
  const { subscription, isTrial, daysRemaining, loading } = useSubscription();
  const { user, loading: authLoading } = useAuth();

  // Hide for: auth loading, superadmin, chain_admin, and chain-managed branch admins
  if (authLoading || loading || !user || user.role === 'superadmin' || user.role === 'chain_admin' || user.chain_id) return null;

  if (!subscription) {
    return (
      <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl" aria-hidden="true">⚠️</span>
          <div>
            <div className="font-semibold text-red-800 dark:text-red-300">{t('subBannerNoActive')}</div>
            <div className="text-sm text-red-600 dark:text-red-400">{t('subBannerNoActiveDesc')}</div>
          </div>
        </div>
        <Link to="/pricing" aria-label={t('subBannerChoosePlan')} className="flex-shrink-0 bg-red-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2">
          {t('subBannerChoosePlan')}
        </Link>
      </div>
    );
  }

  const days = daysRemaining();
  const trial = isTrial();

  if (trial && days > 7) {
    return (
      <div role="status" aria-live="polite" className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl" aria-hidden="true">🎉</span>
          <div>
            <div className="font-semibold text-blue-800 dark:text-blue-300">{t('subBannerTrialHeader')}</div>
            <div className="text-sm text-blue-600 dark:text-blue-400">{days} {t('subBannerTrialDaysLeft')} {t('subBannerTrialHint')}</div>
          </div>
        </div>
        <Link to="/pricing" aria-label={t('subBannerChoosePlan')} className="flex-shrink-0 bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2">
          {t('subBannerChoosePlan')}
        </Link>
      </div>
    );
  }

  if ((trial && days <= 7) || (!trial && days <= 7 && days > 0)) {
    return (
      <div role="status" aria-live="polite" className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-4 mb-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl" aria-hidden="true">⏰</span>
          <div>
            <div className="font-semibold text-amber-800 dark:text-amber-300">
              {trial ? t('subBannerTrialEnding') : t('subBannerSubEnding')}
            </div>
            <div className="text-sm text-amber-700 dark:text-amber-400">
              {days === 0 ? t('subBannerTodayEnd') : `${days} ${t('subBannerDaysLeft')}`} {t('subBannerRenewSoon')}
            </div>
          </div>
        </div>
        <Link to="/my-subscription" aria-label={t('subBannerRenew')} className="flex-shrink-0 bg-amber-500 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-amber-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2">
          {t('subBannerRenew')}
        </Link>
      </div>
    );
  }

  if (days === 0) {
    return (
      <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl p-4 mb-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl" aria-hidden="true">🚫</span>
          <div>
            <div className="font-semibold text-red-800 dark:text-red-300">{t('subBannerExpired')}</div>
            <div className="text-sm text-red-600 dark:text-red-400">{t('subBannerExpiredDesc')}</div>
          </div>
        </div>
        <Link to="/my-subscription" aria-label={t('subBannerRenewNow')} className="flex-shrink-0 bg-red-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2">
          {t('subBannerRenewNow')}
        </Link>
      </div>
    );
  }

  return null;
}
